// Package imports:
import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
// Component imports:
import Label from '../../../ui-elements/Label/Label';
import DisplayBox from '../../DisplayBox/DisplayBox';
import Fyrirvari from '../../Fyrirvari/Fyrirvari';
import Link from '../../../ui-elements/Link/Link';
import Loading from '../../../ui-elements/Loading/Loading';
import LockIcon from '../../../ui-elements/LockIcon/LockIcon';
import Alert from '../../../ui-elements/Alert/Alert';
import ValuationCalculations from './ValuationCalculations';
import ValuationUnsuccessful from './ValuationUnsuccessful';
import SubscriptionBanner from '../../SubscriptionBanner/SubscriptionBanner';
// Service imports:
import { useFetchedApiLmdData } from '../../../services/apiHooks';
import { quantityShorthandSplit, showValuation } from '../../../services/utils';
// Type imports:
import { ICompanyViewModel, ICompanyValuation } from '../../../types/CompanyTypes';

const CompanyValuation: React.FC<ICompanyViewModel> = ({
    id,
    isSubscriber,
    accessToken
}) => {
    const [ disclaimerOpen, setDisclaimerOpen ] = useState(false);
    const openDisclaimer = () => setDisclaimerOpen(true);
    const closeDisclaimer = () => setDisclaimerOpen(false);

    const data = useFetchedApiLmdData<ICompanyValuation>(
        `/v1/market_data/v1/company_valuation/${id}/valuation/v2`,
        accessToken
    );

    const { displayNumber, displayUnit } = useMemo(() => {
        if (data instanceof Error || data === undefined || data == null) return { displayNumber: null, displayUnit: null };
        const { number, unit } = quantityShorthandSplit(data.equity_valuation, 0);
        return { displayNumber: number, displayUnit: unit };
    }, [data]);

    const shouldShowValuation = useMemo(() => {
        if (data instanceof Error) return 'error';
        if (data === null) return 'loading';
        return showValuation(data);
    }, [data])

    const displayValuation = () => {
        return !isSubscriber
            ? <>
                <SubscriptionBanner size='sm' />
                <div className='valuation-footer'>
                    <Link
                        className='more-link'
                        href={`/Fyrirtaeki/Verdmat/${id}`}
                        linkSize='14'
                        disabled
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                        Sjá verðmat
                    </Link>
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
            : data === null
            ? <Loading />
            : !(data instanceof Error) && data.free_cashflow !== null && data.free_cashflow < 0
            ? <>
                <Alert
                    type='info'
                    headText='Fyrirtæki er með neikvætt frjálst sjóðstreymi'
                    text='Ekki er unnt að birta verðmat fyrir fyrirtæki með neikvætt frjálst sjóðstreymi en sjá má útreikning hér fyrir neðan.'
                />
                <div className='valuation-footer'>
                    <ValuationCalculations id={id} data={data} accessToken={accessToken} />
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
            : data instanceof Error
            ? <>
                <Alert
                    type='alert'
                    headText='Ekki tókst að verðmeta fyrirtæki'
                />
                <div className='valuation-footer'>
                    <ValuationUnsuccessful />
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
            : shouldShowValuation === 'negative-fcf'
            ? <>
                <Alert
                    type='info'
                    headText='Fyrirtæki er með neikvætt frjálst sjóðstreymi'
                    text='Ekki er unnt að birta verðmat fyrir fyrirtæki með neikvætt frjálst sjóðstreymi en sjá má útreikning hér fyrir neðan.'
                />
                <div className='valuation-footer'>
                    <ValuationCalculations id={id} data={data} accessToken={accessToken} />
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
            : shouldShowValuation !== 'show'
            ? <>
                <Alert
                    type='alert'
                    headText='Ekki tókst að verðmeta fyrirtæki'
                />
                <div className='valuation-footer'>
                    <ValuationUnsuccessful />
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
            : <>
                <div className='box-wrapper'>
                    <div className='grey-box large-box'>
                        <span className='grey-box__content'>
                            <span className='value'>
                                <span className='value-number'>{displayNumber}</span>
                                <span className='value-unit'>{displayUnit}</span>
                            </span>
                            <span className='title'>Virði eigin fjár</span>
                        </span>
                    </div>
                </div>
                <div className='valuation-footer'>
                    <Link
                        className='more-link'
                        href={`/Fyrirtaeki/Verdmat/${id}`}
                        linkSize='14'
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                        Sjá verðmat
                    </Link>
                    <span className='label-wrapper' onClick={openDisclaimer}>
                        <Label text="Fyrirvari" labelType="disclaimer" />
                    </span>
                </div>
            </>
    }

    return (
        <DisplayBox
            className='KCL_company-valuation'
            titleComponent={
                <>
                    {!isSubscriber && <LockIcon />}
                    Verðmat fyrirtækis
                </>
            }
            statusComponent={<Label labelType='beta' text='Beta' />}
            description={<p className='italic-text'>Keldan reiknar frjálst sjóðstreymi út frá nýjasta ársreikningi.</p>}
        >
            {displayValuation()}
            <Fyrirvari show={disclaimerOpen} close={closeDisclaimer}>
                Verðmatið er ekki fjárfestingarráðgjöf og skal ekki notað til ákvörðunartöku auk þess sem upplýsingar í verðmatinu kunna að vera rangar.
                Þetta verðmat er gert til fróðleiks, samanburðar og skemmtunar en ekki til þess að sýna endanlegt sölu- eða kaupverð félags.<br />
                Keldan ehf. ber enga ábyrgð á röngum upplýsingum eða fjárhagstjóni sem kann að verða vegna verðmatsins.
            </Fyrirvari>
        </DisplayBox>
    );
}

export default CompanyValuation;